import { AuthError, IPublicClientApplication } from "@azure/msal-browser";
import {
  AuthenticatedTemplate,
  MsalProvider,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid2,
  Modal,
} from "@mui/material";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchToolsData } from "../Utils/api";
import { Auth, Code, ErrorType } from "../Utils/Constants";
import { Language, Tool, ToolsDetails } from "../Utils/Interfaces";
import { loginRequest } from "../Utils/msalAuthConfig";
import { getAccessToken } from "../Utils/Utility";
import Footer from "./Footer";
import Header from "./Header";
import "./styles/home.scss";

export const PrestoApplication: React.FC<{
  handleMicrosoftRedirect: any;
  status: string;
  idToken: any;
  accessToken: any;
  msalInstance: IPublicClientApplication;
  inProgressStatus?: any;
}> = ({ ...props }) => {
  const sxProps = {
    mainContainer: {
      marginTop: "30px",
      marginBottom: "30px",
      width: "65%",
      "@media (max-width: 600px)": {
        width: "90%",
        marginBottom: "120px",
      },
    },
    secHeadBox: {
      color: "#555",
      fontWeight: "400",
    },
    hzLine: {
      border: "1px solid #ececec",
      height: "0.5px",
      width: "95%",
      marginBottom: "15px",
      backgroundColor: "#ececec",
    },
    btnBox: {
      display: "flex",
      width: "95%",
      justifyContent: "flex-end",
      marginTop: 2,
    },
    accDetailsDiv: {
      marginBottom: "20px",
      marginTop: "-15px",
    },
    gotoBtn: {
      backgroundColor: "#87d856",
      color: "#000",
      "&:hover": {
        backgroundColor: "#22bb67",
      },
      "&:focus-visible": {
        border: "1.5px solid black",
      },
      borderRadius: "20px",
      marginBottom: "10px",
      textTransform: "none",
      padding: "5px 25px",
      fontFamily: "AvenirNextProMedium, Arial",
    },
    toolCate: {
      "&:focus-visible": {
        border: "1.5px solid black",
      },
    },
    closeBtn: {
      backgroundColor: "#87d856",
      textTransform: "uppercase",
      color: "#000",
      fontWeight: "600",
      "&:hover": {
        backgroundColor: "#22bb67",
      },
      "&:focus-visible": {
        border: "1.5px solid white",
      },
      borderRadius: "20px",
      marginBottom: "10px",
      fontSize: "11px",
      padding: "7px 45px",
      fontFamily: "AvenirNextProMedium, Arial",
    },
    errorGrid1: {
      height: "100vh",
      display: "grid",
      placeContent: "center",
    },
    errorGrid2: {
      position: "relative",
      color: "white",
      backgroundColor: "#2f3336",
      height: "40vh",
      width: "35vw",
      border: "1.5px solid #4c4e52",
      borderRadius: "10px",
      "@media (max-width: 1024px)": {
        height: "35vh",
        width: "65vw",
      },
    },
    loadingTool: {
      fontFamily: "AvenirNextProRegular, Arial",
      fontSize: "1.2rem",
    },
    noTools: {
      fontFamily: "AvenirNextProMedium, Arial",
      backgroundColor: "white",
      color: "#000",
      fontSize: "17px",
      fontWeight: 700,
    },
  };

  const [errorModalOpen, setErrorModalOpen] = React.useState<boolean>(false);
  const [expanded, setExpanded] = useState<number | false>(false);
  const [toolsDetails, setToolsDetails] = useState<ToolsDetails | null>(null);

  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();

  const toggleDropdown = (index: number) => {
    setExpanded((prev) => (prev === index ? false : index));
  };

  useEffect(() => {
    if (error) {
      setErrorModalOpen(true);
    } else {
      setErrorModalOpen(false);
    }
  }, [error]);

  const isStatusActive = useMemo(
    () => props && props.status === Auth.Active && props.accessToken,
    [props]
  );

  const isStatusInActive = useMemo(
    () => props && props.status === Auth.InActive,
    [props]
  );

  useEffect(() => {
    if (isStatusActive) {
      // API to fetch tool details from Backend (Cosmos DB)
      const fetchData = async () => {
        try {
          const response = await fetchToolsData(props.accessToken);

          if (response.status === Code.Success) {
            const finalData = await JSON.parse(response.data);

            if (
              finalData !== ErrorType.TokenExpire &&
              finalData !== ErrorType.ScopeError &&
              finalData !== ErrorType.VersionMissing
            ) {
              if (
                finalData &&
                finalData.ToolsDetails &&
                finalData.ToolsDetails[0].Categories &&
                finalData.ToolsDetails[0].Categories.length > 0
              ) {
                const { ToolsDetails } = finalData;
                setToolsDetails(ToolsDetails[0]);
              } else if (
                finalData &&
                finalData.ToolsDetails &&
                finalData.ToolsDetails[0].Categories &&
                finalData.ToolsDetails[0].Categories.length === 0
              ) {
                setToolsDetails(response);
              } else {
                setError(t("errorFetchingData"));
              }
            }
          } else if (
            response.status === Code.BadRequest ||
            response.status === Code.ServiceUnavailable ||
            response.status === Code.ServerError ||
            response.status === Code.Forbidden ||
            response.status === Code.UnAuthorized
          ) {
            if (
              response.data === ErrorType.TokenExpire ||
              response.data === ErrorType.ScopeError
            ) {
              sessionStorage.clear();
              await props.msalInstance.initialize();
              props.msalInstance
                .loginRedirect({
                  ...loginRequest,
                  prompt: "create",
                })
                .catch((err: AuthError) => {
                  console.log(
                    "Login Error:- Code->",
                    err.errorCode + " | Message->",
                    err.errorMessage
                  );
                });
            } else {
              setError(t("errorFetchingData"));
            }
          } else {
            setError(t("errorFetchingData"));
          }
        } catch (error: any) {
          console.error("Error FetchData->", error);
          setError(t("errorFetchingData"));
        }
      };

      fetchData();
    } else if (isStatusInActive) {
      props.handleMicrosoftRedirect();
    }
  }, [props.status || props.accessToken]);

  const isLoadingToolVersion = useMemo(
    () =>
      props &&
      props.status &&
      props.status === Auth.Active &&
      !toolsDetails &&
      !error,
    [props, toolsDetails, error]
  );

  const isToolCategories = useMemo(
    () => toolsDetails && toolsDetails.Categories,
    [toolsDetails]
  );

  if (isLoadingToolVersion) {
    return (
      <Box
        aria-label={t("loadingToolVersion")}
        component="p"
        sx={sxProps.loadingTool}
      >
        {t("loadingToolVersion")}
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div>
      {props.status === Auth.Active && (
        <>
          <Header handleMicrosoftRedirect={props.handleMicrosoftRedirect} />
          {!error && (
            <>
              {isToolCategories && (
                <Container className="head-container">
                  <Box
                    component="h1"
                    className="primary-head"
                    sx={{ fontFamily: "AvenirNextProBold, Arial" }}
                    aria-label={t("portalTitle")}
                  >
                    {t("portalTitle")}
                  </Box>
                  <Box component="h2" className="secondary-head">
                    <span
                      style={sxProps.secHeadBox}
                      aria-label={t("portalDescription")}
                    >
                      {t("portalDescription")}
                    </span>
                  </Box>
                </Container>
              )}

              {!isToolCategories && !error && (
                <Container className="head-container">
                  <Alert sx={sxProps.noTools} icon={false}>
                    {t("noTools")}
                  </Alert>
                </Container>
              )}

              <Container sx={sxProps.mainContainer}>
                {toolsDetails &&
                  toolsDetails.Categories &&
                  toolsDetails.Categories.map((category, categoryIndex) => (
                    <Accordion
                      expanded={expanded === categoryIndex}
                      onChange={() => toggleDropdown(categoryIndex)}
                      key={categoryIndex}
                      className="accord-details"
                      sx={{
                        boxShadow:
                          expanded === categoryIndex
                            ? "0 4px 20px 0 rgba(0, 0, 0, 0.2), 4px 2px 10px 4px rgba(208, 239, 189)"
                            : "none",
                        backgroundColor: "#f4f4f4",
                        padding: "10px",
                        border: "1px solid #d6d6d6",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={
                          <ExpandMoreIcon style={{ color: "#000" }} />
                        }
                        aria-controls={`panel${categoryIndex}-content`}
                        id={`panel${categoryIndex}-header`}
                        sx={sxProps.toolCate}
                      >
                        <Typography
                          sx={{
                            fontFamily: "AvenirNextProBold, Arial",
                          }}
                        >
                          {category[i18n.language as Language]?.CategoryName}
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        {category[i18n.language as Language]?.Tools.map(
                          (tool: Tool, toolIndex: number) => (
                            <div key={toolIndex} style={sxProps.accDetailsDiv}>
                              {category[i18n.language as Language].Tools
                                .length > 1 && (
                                  <>
                                    <div style={sxProps.hzLine} />
                                    <Typography
                                      component="h4"
                                      sx={{
                                        fontSize: "16px",
                                        fontFamily: "AvenirNextProMedium, Arial",
                                      }}
                                      aria-label={tool.Name}
                                    >
                                      {tool.Name}
                                    </Typography>
                                  </>
                                )}
                              <Typography
                                component="p"
                                sx={{
                                  color: "#555",
                                  fontFamily: "AvenirNextProRegular, Arial",
                                }}
                                aria-label={tool.Description}
                              >
                                {tool.Description}
                              </Typography>
                              <Box sx={sxProps.btnBox}>
                                <Button
                                  className="goto-btn"
                                  data-testid="goToBtn"
                                  variant="contained"
                                  sx={sxProps.gotoBtn}
                                  onClick={() =>
                                    window.open(tool.Url, "_blank","noopener,noreferrer")
                                  }
                                  aria-label={`${tool.Name} ${t(
                                    "goToToolBtnA"
                                  )}`}
                                  disableRipple
                                >
                                  {t("goToTool")}
                                </Button>
                              </Box>
                            </div>
                          )
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
              </Container>
            </>
          )}
          {error && (
            <Modal
              open={errorModalOpen}
              aria-labelledby="Technical Error"
              aria-describedby={error}
            >
              <Grid2 sx={sxProps.errorGrid1}>
                <Grid2 sx={sxProps.errorGrid2}>
                  <Container
                    sx={{
                      display: "grid",
                      placeContent: "center",
                      height: "100px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "AvenirNextProMedium, Arial",
                        fontSize: "25px",
                        color: "#f9f9f9",
                      }}
                      variant="h1"
                      aria-label={t("technicalError")}
                    >
                      {t("technicalError")}
                    </Typography>
                  </Container>
                  <Container
                    sx={{
                      display: "grid",
                      placeContent: "center",
                      height: "20px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "AvenirNextProMedium, Arial",
                        fontSize: "14px",
                        color: "#f9f9f9",
                      }}
                      aria-label={error}
                      component="p"
                    >
                      {error}
                    </Typography>
                  </Container>
                  <Container
                    sx={{
                      display: "grid",
                      placeContent: "center",
                      height: "100px",
                    }}
                  >
                    <Button
                      variant="contained"
                      className="goto-btn"
                      data-testid="closeBtn"
                      sx={sxProps.closeBtn}
                      aria-label={t("closeBtn")}
                      onClick={props.handleMicrosoftRedirect}
                      disableRipple
                    >
                      {t("closeBtn")}
                    </Button>
                  </Container>
                </Grid2>
              </Grid2>
            </Modal>
          )}
          <Footer />
        </>
      )}
    </div>
  );
};

export const WrappedAuthView: React.FC = () => {
  const [idToken, setIdToken] = useState<string>("");
  const [accessToken, setAccessToken] = useState<string>("");

  const { instance, inProgress } = useMsal();
  const activeAccount = instance.getActiveAccount();

  // Method to provide SSO B2B Login
  const handleLoginRedirect = () => {
    instance
      .loginRedirect({
        ...loginRequest,
        prompt: "create",
      })
      .catch((err: AuthError) => {
        console.error(
          "Login Error:- Message->",
          err.errorMessage + " | Code->",
          err.errorCode
        );
      });
  };

  // Method to Sign Out currently logged in user
  const handleLogoutRedirect = async () => {
    try {
      await instance.logoutRedirect();
    } catch (error: any) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (activeAccount && activeAccount.idToken) {
      setIdToken(activeAccount.idToken);
    }
  }, [activeAccount]);

  useEffect(() => {
    if (activeAccount && instance) {
      const acquiredAccessToken = getAccessToken();
      setAccessToken(acquiredAccessToken);
    }
  }, [activeAccount, instance]);

  return (
    <>
      <AuthenticatedTemplate>
        {activeAccount && accessToken && (
          <PrestoApplication
            handleMicrosoftRedirect={handleLogoutRedirect}
            status={Auth.Active}
            idToken={idToken}
            accessToken={accessToken}
            msalInstance={instance}
          />
        )}
      </AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <PrestoApplication
          handleMicrosoftRedirect={handleLoginRedirect}
          status={Auth.InActive}
          idToken={idToken}
          accessToken={accessToken}
          msalInstance={instance}
          inProgressStatus={inProgress}
        />
      </UnauthenticatedTemplate>
    </>
  );
};

const Home: React.FC<{ msalInstance: any }> = ({ ...props }) => {
  const { msalInstance } = props;

  return (
    <MsalProvider instance={msalInstance}>
      <WrappedAuthView />
    </MsalProvider>
  );
};

export default Home;
